import { HttpResponse } from '@angular/common/http';

/**
 * Saves a report download response to disk under the filename the server suggests.
 *
 * The name comes from the `Content-Disposition` header (`filename*=` preferred over `filename=`),
 * falling back to `fallbackName`. A `.zip` or `.pdf` extension (by content type, `.pdf` otherwise)
 * is appended when the name has none. The body is saved through a temporary object-URL anchor click.
 * @param response - The blob HTTP response of the report download request.
 * @param fallbackName - Filename used when the headers carry none.
 * @param basicUsesFullMatch - When true, a plain `filename=` header yields the whole regex match
 * (`filename="x.pdf"`) rather than just the captured name. Kept only because the weekly report
 * has always behaved this way; the other callers pass false.
 */
export function saveReportDownload(
  response: HttpResponse<Blob>,
  fallbackName: string,
  basicUsesFullMatch = false
): void {
  const contentDisposition = response.headers.get('content-disposition') || '';
  const contentType = response.headers.get('Content-Type')?.toLowerCase() || '';

  let filename = fallbackName;

  const mStar = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  const mBasic = contentDisposition.match(/filename="?([^"]+)"?/i);

  if (mStar && mStar[1]) {
    filename = decodeURIComponent(mStar[1]);
  } else if (mBasic && mBasic[1]) {
    filename = basicUsesFullMatch ? mBasic[0] : mBasic[1];
  }

  const hasExtension = /\.[a-z0-9]+$/i.test(filename);
  if (!hasExtension) {
    if (contentType.includes('zip')) {
      filename += '.zip';
    } else {
      filename += '.pdf';
    }
  }

  const blob = new Blob([response.body!], {
    type: contentType || 'application/octet-stream',
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
